import React, { useState } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Sidebar = ({
  categories,
  selectedCategory,
  onCategorySelect,
  onCategoryCreated,
  collapsed
}) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParent, setNewCategoryParent] = useState(null);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);

  const toggleExpand = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newCategoryName,
          parent_id: newCategoryParent
        })
      });

      if (response.ok) {
        toast.success('Category created');
        setNewCategoryName('');
        setShowNewCategoryInput(false);
        setNewCategoryParent(null);
        onCategoryCreated();
      } else {
        throw new Error('Failed to create category');
      }
    } catch (error) {
      console.error('Failed to create category:', error);
      toast.error('Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this category and all subcategories?')) return;

    try {
      const response = await fetch(`${API_URL}/api/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Category deleted');
        onCategoryCreated();
      } else {
        throw new Error('Failed to delete category');
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    }
  };

  const renderCategoryTree = (parentId = null, level = 0) => {
    const childCategories = categories.filter(c => c.parent_id === parentId);

    return childCategories.map(category => {
      const hasChildren = categories.some(c => c.parent_id === category.category_id);
      const isExpanded = expandedCategories.has(category.category_id);
      const isSelected = selectedCategory === category.category_id;

      return (
        <div key={category.category_id} style={{ marginLeft: `${level * 16}px` }}>
          <div
            className={`tree-item group ${isSelected ? 'active' : ''}`}
            onClick={() => onCategorySelect(category.category_id)}
            data-testid={`category-${category.category_id}`}
          >
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(category.category_id);
                }}
                className="p-1 hover:bg-accent/20 rounded"
              >
                <svg
                  className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            {!hasChildren && <div className="w-5" />}
            
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            
            <span className="flex-1 truncate">{category.name}</span>
            
            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNewCategoryParent(category.category_id);
                  setShowNewCategoryInput(true);
                }}
                className="p-1 hover:bg-accent/20 rounded"
                title="Add subcategory"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={(e) => handleDeleteCategory(category.category_id, e)}
                className="p-1 hover:bg-destructive/20 rounded text-destructive"
                title="Delete category"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>

          {hasChildren && isExpanded && renderCategoryTree(category.category_id, level + 1)}
        </div>
      );
    });
  };

  if (collapsed) {
    return (
      <div className="sidebar w-16 flex flex-col items-center py-4">
        <button
          onClick={() => onCategorySelect(null)}
          className={`p-3 rounded-lg ${!selectedCategory ? 'bg-primary/10 text-primary' : 'hover:bg-accent/10'}`}
          title="All Bookmarks"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <aside className="flex flex-col h-full">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-foreground">Categories</h2>
          <button
            onClick={() => {
              setNewCategoryParent(null);
              setShowNewCategoryInput(true);
            }}
            className="p-2 hover:bg-accent/10 rounded-lg transition-colors"
            title="New category"
            data-testid="new-category-button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {showNewCategoryInput && (
          <div className="mb-4 p-3 bg-secondary/50 rounded-lg">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Category name..."
              className="w-full px-3 py-2 mb-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-sm"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
              data-testid="new-category-input"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreateCategory}
                className="flex-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm"
                data-testid="save-category-button"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setShowNewCategoryInput(false);
                  setNewCategoryName('');
                  setNewCategoryParent(null);
                }}
                className="flex-1 px-3 py-1.5 border border-input rounded-md hover:bg-accent/10 text-sm"
                data-testid="cancel-category-button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* All Bookmarks */}
        <div
          className={`tree-item mb-2 ${!selectedCategory ? 'active' : ''}`}
          onClick={() => onCategorySelect(null)}
          data-testid="category-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <span>All Bookmarks</span>
        </div>

        {/* Category Tree */}
        <div className="space-y-1">
          {renderCategoryTree()}
        </div>

        {categories.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <p>No categories yet.</p>
            <p className="mt-2">Click + to create one</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
